import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

/**
 * Lifted chat session state shared between the /chat tab and the analytics
 * drawer. Both surfaces address the same session_id so their histories
 * stay in sync. The actual send/fetch logic lives in useChatSession;
 * this context is storage only.
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  retrieved_chunks?: Array<{ document_title: string; similarity: number }>;
  attachments?: Array<{
    chart_id: string;
    title: string;
    period: { start: string; end: string };
    athletes: string[];
    data: { kind: string; rows?: unknown[]; series?: unknown[] };
  }>;
}

interface ChatSessionContextValue {
  sessionId: string | null;
  setSessionId: (id: string | null) => void;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  isSending: boolean;
  setIsSending: (sending: boolean) => void;
}

const ChatSessionContext = createContext<ChatSessionContextValue | null>(null);

export const ChatSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);

  const value = useMemo(
    () => ({ sessionId, setSessionId, messages, setMessages, isSending, setIsSending }),
    [sessionId, messages, isSending]
  );

  return <ChatSessionContext.Provider value={value}>{children}</ChatSessionContext.Provider>;
};

export const useChatSessionContext = (): ChatSessionContextValue => {
  const ctx = useContext(ChatSessionContext);
  if (!ctx) {
    throw new Error('useChatSessionContext must be used inside <ChatSessionProvider>');
  }
  return ctx;
};
